// Again home (Task 4): Women/Men tabs for the styles grid, and feature loops that
// load and play only while on screen (muted, looped, inline; paused under reduced motion).
class AghTabs extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const tabs = [...this.querySelectorAll('[role="tab"]')];
    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        const panel = this.querySelector('#' + t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      const link = this.querySelector('[data-agh-all]');
      if (link && tab.dataset.url) {
        link.href = tab.dataset.url;
        link.querySelector('span').textContent = tab.dataset.label;
      }
      if (focus) tab.focus();
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        select(next, true);
      });
    });
  }
}
if (!customElements.get('agh-tabs')) customElements.define('agh-tabs', AghTabs);

class AghLoop extends HTMLElement {
  // The <video> ships with preload="none", no autoplay and no src (data-src): nothing downloads until
  // the tile is on screen. First intersect → set src + play; off screen → pause. Reduced motion or no
  // IntersectionObserver → never loads, the static poster stays.
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video || this._io || !('IntersectionObserver' in window)) return;
    video.muted = true;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !reduce.matches) {
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
              video.preload = 'auto';
            }
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
          } else if (video.src) {
            video.pause();
          }
        });
      },
      { threshold: 0.2 }
    );
    this._io.observe(this);
  }
  disconnectedCallback() {
    this._io?.disconnect();
    this._io = null;
  }
}
if (!customElements.get('agh-loop')) customElements.define('agh-loop', AghLoop);

// Agitation band (copy deck v3 §3.2): lines reveal one by one as they scroll into view.
// Lines are visible by default (no JS = readable). The band is armed (lines hidden) only when
// IntersectionObserver exists and the visitor has not asked for reduced motion; lines already on
// screen when it arms are shown first, so nothing flickers.
class AghAgitate extends HTMLElement {
  connectedCallback() {
    if (this._io || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const lines = [...this.querySelectorAll('.agh-agit__line')];
    if (!lines.length) return;
    const vh = window.innerHeight || 800;
    lines.forEach((l) => { if (l.getBoundingClientRect().top < vh * 0.85) l.classList.add('is-in'); });
    this.classList.add('is-armed');
    let queue = [];
    let timer = null;
    const flush = () => {
      const next = queue.shift();
      if (next) next.classList.add('is-in');
      timer = queue.length ? setTimeout(flush, 160) : null;
    };
    this._io = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => lines.indexOf(a.target) - lines.indexOf(b.target))
          .forEach((e) => {
            this._io.unobserve(e.target);
            if (!e.target.classList.contains('is-in')) queue.push(e.target);
          });
        if (queue.length && !timer) flush();
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.4 }
    );
    lines.forEach((l) => { if (!l.classList.contains('is-in')) this._io.observe(l); });
  }
  disconnectedCallback() {
    this._io?.disconnect();
    this._io = null;
  }
}
if (!customElements.get('agh-agitate')) customElements.define('agh-agitate', AghAgitate);
